(ns frontend.view.settings2.counterparty-diligence.view
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.entities.counterparty-diligence.notifications :as cd-notifications]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.view.not-found :as not-found]
   [frontend.view.settings2.counterparty-diligence.event :as event]
   [frontend.view.settings2.counterparty-diligence.sub :as sub]
   [frontend.view.settings2.sales-pipelines.db :as db]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]))

(def ^:private lang-kw :en)

(defn breadcrumbs
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [_]
                 (routing/set-view! [:account-settings :tab-key "config"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :settings/counterparty-diligence)]])

(defn- recipients-input
  [{:keys [id]}]
  (let [value @(rf/subscribe [::sub/recipients-input id])
        invalid @(rf/subscribe [::sub/invalid-emails id])]
    [:div
     {:class "flex flex-col gap-1"}
     [labelled-input/view
      {:label       (loc/tr :counterparty-diligence-settings/recipients-label)
       :data-type   :data.type/text-simple
       :placeholder (loc/tr :counterparty-diligence-settings/recipients-placeholder)
       :value       (or value "")
       :on-change   (fn [new-val]
                      (rf/dispatch [::event/set-recipients-input id new-val]))}]
     (when (seq invalid)
       [:p
        {:class "text-xs m-0 text-danger-regular"}
        (loc/tr :counterparty-diligence-settings/invalid-emails
                [(string/join ", " invalid)])])]))

(defn- variant-tabs
  [{:keys [variants active-template-id]}]
  [:div
   {:class "flex gap-1 border-b border-base-light-border-paler"}
   (->> variants
        (map (fn [{:keys [template-id label]}]
               (let [active? (= template-id active-template-id)]
                 ^{:key template-id}
                 [:button
                  {:class    (str "px-3 py-2 text-sm bg-transparent border-0 border-b-2 cursor-pointer "
                                  (if active?
                                    "border-primary-regular font-semibold text-base-light-text"
                                    "border-transparent text-base-light-text-paler hover:text-base-light-text"))
                   :on-click #(rf/dispatch [::event/select-preview-variant template-id])}
                  (get label lang-kw)])))
        (doall))])

(defn- preview-modal
  []
  (let [notification @(rf/subscribe [::sub/preview-notification])
        variant      @(rf/subscribe [::sub/preview-variant])
        open?        (some? notification)
        close!       #(rf/dispatch [::event/close-preview])]
    [modal/view
     {:visible? open?
      :hide! close!}
     [:div
      {:class "text-lg font-semibold"}
      (loc/tr :counterparty-diligence-settings/preview-title)]
     (when notification
       [:div
        {:class "flex flex-col gap-4"}
        (when (next (:variants notification))
          [variant-tabs
           {:variants (:variants notification)
            :active-template-id (:template-id variant)}])
        (when variant
          (let [{:keys [subject body sample-context]} variant
                rendered-subject (cd-notifications/render subject sample-context)
                rendered-body    (cd-notifications/render body sample-context)]
            [:div
             {:class "flex flex-col gap-4"}
             [:div
              {:class "flex flex-col gap-1"}
              [:span
               {:class "text-xs uppercase tracking-wide m-0 text-base-light-text-paler"}
               (loc/tr :counterparty-diligence-settings/preview-subject-label)]
              [:p
               {:class "font-medium m-0"}
               rendered-subject]]
             [:div
              {:class "flex flex-col gap-1"}
              [:span
               {:class "text-xs uppercase tracking-wide m-0 text-base-light-text-paler"}
               (loc/tr :counterparty-diligence-settings/preview-body-label)]
              [:pre
               {:class "whitespace-pre-wrap m-0 p-4 rounded border bg-base-light text-sm leading-relaxed"
                :style {:font-family "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"}}
               rendered-body]]]))])
     [:<>
      [button/sm-secondary-hollow
       {:on-click close!}
       (loc/tr :close)]]]))

(defn- notification-card
  [{:keys [id label description]} lang-kw]
  [:div
   {:class "flex flex-col gap-5 rounded border shadow p-4"}
   [:div
    {:class "flex items-start justify-between gap-3"}
    [:div
     {:class "flex flex-col gap-1"}
     [:h4
      {:class "text-lg font-semibold m-0"}
      (get label lang-kw)]
     (when-let [desc (get description lang-kw)]
       [:p
        {:class "text-sm m-0 text-base-light-text-paler"}
        desc])]
    [button/sm-secondary-hollow
     {:on-click #(rf/dispatch [::event/open-preview id])}
     [:i {:class "fal fa-eye pr-2"}]
     (loc/tr :counterparty-diligence-settings/show-preview)]]
   [recipients-input {:id id}]])

(defn- footer
  []
  (let [changed? @(rf/subscribe [::sub/config-changed?])
        saving?  @(rf/subscribe [::sub/saving?])]
    [:div
     {:class "flex items-center justify-end gap-2 pt-4"}
     [button/sm-primary-solid
      {:disabled? (or saving? (not changed?))
       :on-click  #(rf/dispatch [::event/save-changes])}
      (if saving? (loc/tr :saving) (loc/tr :save))]]))

(defn page-body
  []
  (let [notifications @(rf/subscribe [::sub/notifications])]
    [:div
     {:class "flex flex-col gap-5"}
     [:div
      [:h2
       {:class "text-2xl font-semibold m-0"}
       (loc/tr :counterparty-diligence-settings/notifications-heading)]
      [:p
       {:class "text-base-light-text-paler"}
       (loc/tr :counterparty-diligence-settings/notifications-info)]]

     (if (empty? notifications)
       [:p (loc/tr :counterparty-diligence-settings/no-notifications-configured)]
       (->> notifications
            (map (fn [n]
                   ^{:key (:id n)}
                   [notification-card n lang-kw]))
            (doall)))

     [footer]
     [preview-modal]]))

(defn page
  []
  (rf/dispatch-sync [::event/init-editor])
  (r/create-class
   {:reagent-render
    (fn []
      [:<>
       [zap-toolbar/view db/state breadcrumbs nil (constantly [])]
       [:div
        {:class "bg-base-light flex flex-col items-center py-10 px-4"}
        [:div
         {:class "w-full max-w-4xl"}
         [page-body]]]])}))

(defmethod routing/render-view :settings/counterparty-diligence
  [_]
  (if (and (session/is-admin?)
           (session/has-feature-access? :feature/counterparty-diligence))
    [default-page-layout/view
     [page]]
    [not-found/not-found-view]))
